import { useState } from "react";
import { Share2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Share + copy-link buttons for public seller and partner profiles.
 * Uses the native share sheet when available, otherwise falls back to
 * clipboard copy. Purely presentational — no auction logic.
 */
export function ShareProfileButton({
  url,
  title,
  text,
  className,
}: {
  url: string;
  title: string;
  text?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const share = async () => {
    try {
      if (canShare) {
        await navigator.share({ url, title, text });
        return;
      }
      await copy();
    } catch {
      // user cancelled — noop
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Profile link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className={"inline-flex items-center gap-2 " + (className ?? "")}>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={share}
        className="h-8 gap-1.5 rounded-full border-border/60 text-xs"
        aria-label="Share profile"
      >
        <Share2 className="h-3.5 w-3.5" aria-hidden />
        Share
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={copy}
        className="h-8 gap-1.5 rounded-full border-border/60 text-xs"
        aria-label="Copy profile link"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
        {copied ? "Copied" : "Copy link"}
      </Button>
    </div>
  );
}
